import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  Space,
  Typography,
  Tabs,
  Form,
  Input,
  Select,
  Alert,
  Upload,
  Table,
  Modal,
  Tag,
  Popconfirm,
  Divider,
  Image,
  message,
  Spin
} from 'antd';
import {
  UserOutlined,
  DatabaseOutlined,
  SaveOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PhoneOutlined,
  MailOutlined,
  UploadOutlined,
  EyeOutlined,
  GoogleOutlined,
  DisconnectOutlined,
  TagsOutlined,
  TeamOutlined,
  StarOutlined
} from '@ant-design/icons';
import apiService from '../../../services/apiService';
import CustomAttributesSettings from '../components/CustomAttributesSettings';
import TeamManagement from '../components/TeamManagement';
import ClientTiersSettings from '../components/ClientTiersSettings';
import CreditAllocationPanel from '../../billing/components/CreditAllocationPanel';
import { useAuthStore } from '../../../stores/authStore';
import { usePermissions } from '../../../hooks/usePermissions';

// Google Client ID
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          prompt: () => void;
        };
      };
    };
  }
}

const { Title } = Typography;
const { Option } = Select;

// TypeScript interfaces
interface CompanyAddress {
  id: string;
  type: 'delivery' | 'correspondence' | 'billing' | 'other';
  contactName: string;
  detailedAddress: string;
  country: string;
  phone?: string;
  isDefault: boolean;
}

interface CompanySettings {
  companyName: string;
  /** User-facing "Company ID" (Company.code) — the shared login key. */
  companyCode: string;
  companyLogo: string;
  companyContactNumber: string;
  companyEmail: string;
  description: string;
  industry: string;
  services: string;
  companyAddresses: CompanyAddress[];
}

const SettingsOverview: React.FC = () => {
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const canViewTeam = hasPermission('team:read');

  const getActiveTabFromRoute = () => {
    const path = location.pathname;
    if (path.includes('/settings/profile')) return 'profile';
    if (path.includes('/settings/company')) return 'company';
    if (path.includes('/settings/team')) return 'team';
    return 'profile';
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromRoute());
  const [loading, setLoading] = useState(false);

  // Auth store for profile
  const { user, linkGoogle, unlinkGoogle, checkGoogleLinked, isLoading: authLoading } = useAuthStore();
  const [isGoogleLinked, setIsGoogleLinked] = useState(false);
  const [googleLinkLoading, setGoogleLinkLoading] = useState(false);

  // Company settings state
  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    companyName: 'WYNI AI ERP',
    companyCode: '',
    companyLogo: '',
    companyContactNumber: '',
    companyEmail: '',
    description: '',
    industry: '',
    services: '',
    companyAddresses: []
  });

  // Forms
  const [companyForm] = Form.useForm();
  const [addressForm] = Form.useForm();

  // Address management state
  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false);
  const [editingAddress, setEditingAddress] = useState<CompanyAddress | null>(null);

  // Logo preview state
  const [logoPreview, setLogoPreview] = useState<string>('');

  // Check Google linked status on mount
  useEffect(() => {
    const checkStatus = async () => {
      const linked = await checkGoogleLinked();
      setIsGoogleLinked(linked);
    };
    checkStatus();
  }, [checkGoogleLinked]);

  // Handle Google link callback
  const handleGoogleLinkCallback = useCallback(async (response: any) => {
    try {
      setGoogleLinkLoading(true);
      const payload = JSON.parse(atob(response.credential.split('.')[1]));
      const googleId = payload.sub;
      const email = payload.email;

      await linkGoogle({ googleId, email });
      setIsGoogleLinked(true);
      message.success('Google 帳戶綁定成功！下次可以直接使用 Google 登錄。');
    } catch (error: any) {
      message.error(error.message || '綁定 Google 帳戶失敗');
    } finally {
      setGoogleLinkLoading(false);
    }
  }, [linkGoogle]);

  // Initialize Google Sign-In for linking
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || activeTab !== 'profile') return;

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleLinkCallback,
        });

        const buttonDiv = document.getElementById('google-link-button');
        if (buttonDiv && !isGoogleLinked) {
          window.google.accounts.id.renderButton(buttonDiv, {
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
          });
        }
      }
    };
    document.body.appendChild(script);

    return () => {
      const scripts = document.querySelectorAll('script[src="https://accounts.google.com/gsi/client"]');
      scripts.forEach(s => s.remove());
    };
  }, [handleGoogleLinkCallback, activeTab, isGoogleLinked]);

  // Handle unlink Google
  const handleUnlinkGoogle = async () => {
    try {
      setGoogleLinkLoading(true);
      await unlinkGoogle();
      setIsGoogleLinked(false);
      message.success('已解除 Google 帳戶綁定');
    } catch (error: any) {
      message.error(error.message || '解除綁定失敗');
    } finally {
      setGoogleLinkLoading(false);
    }
  };

  // 初始化/刷新載入公司設定
  const loadCompany = useCallback(async () => {
    try {
      setLoading(true);
      const resp: any = await apiService.getCompanySettings();
      const data = resp?.data || resp; // 後端統一 {success,data}；保守處理
      if (data?.settings) {
        const s = data.settings;
        const addr = data.addresses || [];
        const newState = {
          companyName: s.companyName || '',
          companyCode: s.companyCode || '',
          companyLogo: s.companyLogo || '',
          companyContactNumber: s.companyContactNumber || '',
          companyEmail: s.companyEmail || '',
          description: s.description || '',
          industry: s.industry || '',
          services: s.services || '',
          companyAddresses: addr,
        } as CompanySettings;
        setCompanySettings(newState);
        companyForm.setFieldsValue({
          companyName: newState.companyName,
          companyCode: newState.companyCode,
          companyContactNumber: newState.companyContactNumber,
          companyEmail: newState.companyEmail,
          companyLogo: newState.companyLogo,
          description: newState.description,
          industry: newState.industry,
          services: newState.services,
        });
        setLogoPreview(newState.companyLogo || '');
      }
    } catch (e) {
      // ignore -> 預設 mock
    } finally {
      setLoading(false);
    }
  }, [companyForm]);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  // Company settings handlers
  const handleLogoUpload = (file: any) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setLogoPreview(result);
      setCompanySettings(prev => ({ ...prev, companyLogo: result }));
    };
    reader.readAsDataURL(file);
    return false; // Prevent default upload
  };

  // Only owners/admins may change the shared Company ID. Mirrors the server check.
  const canEditCompanyCode = ['ADMIN', 'SUPER_ADMIN'].includes(String(user?.role || '').toUpperCase())
    || ['OWNER', 'ADMIN'].includes(String(user?.companyRoleCode || '').toUpperCase());

  const submitCompanySettings = async (values: any) => {
    try {
      setLoading(true);
      const nextCode = String(values.companyCode || '').trim().toUpperCase();
      const payload: any = {
        companyName: values.companyName,
        ...(canEditCompanyCode && nextCode ? { companyCode: nextCode } : {}),
        companyContactNumber: values.companyContactNumber,
        companyEmail: values.companyEmail,
        companyLogo: companySettings.companyLogo || logoPreview || '',
        description: values.description || '',
        industry: values.industry || '',
        services: values.services || '',
      };
      const resp: any = await apiService.updateCompanySettings(payload);
      const updated = resp?.data || resp;
      if (updated) {
        setCompanySettings(prev => ({ ...prev, ...payload }));
      }
      // Keep the in-memory user in sync so the Company ID card reflects the change.
      if (payload.companyCode) {
        useAuthStore.setState((st: any) =>
          st.user ? { user: { ...st.user, companyCode: payload.companyCode } } : {}
        );
      }
      // 重新讀取地址（維持一致）
      const adrResp: any = await apiService.listCompanyAddresses();
      const addrs = adrResp?.data || adrResp || [];
      setCompanySettings(prev => ({ ...prev, companyAddresses: Array.isArray(addrs) ? addrs : [] }));
      message.success('Company settings saved successfully');
    } catch (error: any) {
      // Surface the server's reason (e.g. duplicate Company ID) instead of a generic failure.
      message.error(
        error?.response?.data?.message || error?.message || 'Failed to save company settings'
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Changing the Company ID changes the login key for every user in the company,
   * so confirm before saving it.
   */
  const handleSaveCompanySettings = async (values: any) => {
    const nextCode = String(values.companyCode || '').trim().toUpperCase();
    const currentCode = String(companySettings.companyCode || '').toUpperCase();

    if (canEditCompanyCode && nextCode && nextCode !== currentCode) {
      Modal.confirm({
        title: 'Change Company ID?',
        content: (
          <div>
            <p>
              The Company ID will change from <strong>{currentCode || '—'}</strong> to{' '}
              <strong>{nextCode}</strong>.
            </p>
            <p style={{ marginBottom: 0 }}>
              Everyone in your company must use the new Company ID the next time they sign in.
              Please let your team know before continuing.
            </p>
          </div>
        ),
        okText: 'Change Company ID',
        cancelText: 'Cancel',
        onOk: () => submitCompanySettings(values),
      });
      return;
    }

    await submitCompanySettings(values);
  };

  // Address management functions
  const handleAddAddress = () => {
    setEditingAddress(null);
    addressForm.resetFields();
    setIsAddressModalVisible(true);
  };

  const handleEditAddress = (address: CompanyAddress) => {
    setEditingAddress(address);
    addressForm.setFieldsValue(address);
    setIsAddressModalVisible(true);
  };

  const handleDeleteAddress = async (addressId: string) => {
    try {
      setLoading(true);
      const resp: any = await apiService.deleteCompanyAddress(addressId);
      const addrs = resp?.data || resp || [];
      setCompanySettings(prev => ({ ...prev, companyAddresses: Array.isArray(addrs) ? addrs : [] }));
      message.success('Address deleted successfully');
    } catch {
      message.error('Failed to delete address');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAddress = async (values: any) => {
    try {
      setLoading(true);
      const payload = {
        id: editingAddress?.id,
        ...values,
        isDefault: editingAddress ? values.isDefault ?? editingAddress.isDefault : companySettings.companyAddresses.length === 0,
      };
      const resp: any = await apiService.upsertCompanyAddress(payload);
      const addrs = resp?.data || resp || [];
      setCompanySettings(prev => ({ ...prev, companyAddresses: Array.isArray(addrs) ? addrs : [] }));
      setIsAddressModalVisible(false);
      message.success(`Address ${editingAddress ? 'updated' : 'added'} successfully`);
    } catch (error) {
      message.error('Failed to save address');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefaultAddress = async (addressId: string) => {
    try {
      const found = companySettings.companyAddresses.find(a => a.id === addressId);
      if (!found) return;
      setLoading(true);
      const resp: any = await apiService.upsertCompanyAddress({
        id: found.id,
        type: found.type,
        contactName: found.contactName,
        detailedAddress: found.detailedAddress,
        country: found.country,
        phone: found.phone,
        isDefault: true,
      });
      const addrs = resp?.data || resp || [];
      setCompanySettings(prev => ({ ...prev, companyAddresses: Array.isArray(addrs) ? addrs : [] }));
      message.success('Default address updated');
    } catch {
      message.error('Failed to set default address');
    } finally {
      setLoading(false);
    }
  };

  const renderProfileSettings = () => (
    <div>
      {/* User Profile Card */}
      <Card title="User Profile" style={{ marginBottom: 24 }}>
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 16 }}>
              <Typography.Text type="secondary">Name</Typography.Text>
              <Typography.Title level={5} style={{ margin: '4px 0' }}>
                {user?.name || 'Not set'}
              </Typography.Title>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 16 }}>
              <Typography.Text type="secondary">Email</Typography.Text>
              <Typography.Title level={5} style={{ margin: '4px 0' }}>
                {user?.email || 'Not set'}
              </Typography.Title>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 16 }}>
              <Typography.Text type="secondary">Role</Typography.Text>
              <div style={{ marginTop: 4 }}>
                <Tag color={user?.role === 'ADMIN' ? 'red' : user?.role === 'MANAGER' ? 'orange' : 'blue'}>
                  {user?.role || 'USER'}
                </Tag>
              </div>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 16 }}>
              <Typography.Text type="secondary">Company ID</Typography.Text>
              <Typography.Title level={5} style={{ margin: '4px 0' }}>
                {user?.companyCode || companySettings.companyCode || 'Not assigned'}
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Used by everyone in your company to sign in.
              </Typography.Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Google Account Linking Card */}
      <Card
        title={
          <Space>
            <GoogleOutlined style={{ color: '#4285f4', fontSize: 20 }} />
            <span>Google Account</span>
          </Space>
        }
      >
        <Spin spinning={googleLinkLoading || authLoading}>
          {isGoogleLinked ? (
            <div>
              <Alert
                message="Google 帳戶已綁定"
                description="您可以使用 Google 帳戶直接登錄，無需輸入公司 ID、郵箱和密碼。"
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Popconfirm
                title="確定要解除綁定 Google 帳戶嗎？"
                description="解除綁定後，您將需要使用公司 ID、郵箱和密碼登錄。"
                onConfirm={handleUnlinkGoogle}
                okText="確定"
                cancelText="取消"
              >
                <Button
                  danger
                  icon={<DisconnectOutlined />}
                  loading={googleLinkLoading}
                >
                  解除綁定 Google 帳戶
                </Button>
              </Popconfirm>
            </div>
          ) : (
            <div>
              <Alert
                message="尚未綁定 Google 帳戶"
                description="綁定 Google 帳戶後，您可以使用 Google 一鍵登錄，無需輸入公司 ID、郵箱和密碼。"
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              {GOOGLE_CLIENT_ID ? (
                <div>
                  <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    點擊下方按鈕，使用您的 Google 帳戶進行綁定：
                  </Typography.Text>
                  <div id="google-link-button" style={{ display: 'inline-block' }} />
                </div>
              ) : (
                <Alert
                  message="Google 登錄未配置"
                  description="請聯繫管理員配置 Google OAuth Client ID。"
                  type="warning"
                  showIcon
                />
              )}
            </div>
          )}
        </Spin>
      </Card>
    </div>
  );

  const renderCompanySettings = () => (
    <div>
      {/* Company Information Card */}
      <Card title="Company Information" style={{ marginBottom: 24 }} id="tour-company-info">
        <Form
          form={companyForm}
          layout="vertical"
          initialValues={companySettings}
          onFinish={handleSaveCompanySettings}
        >
                      <Row gutter={[24, 16]}>
            {/* Left side - Company Information */}
            <Col xs={24} lg={16}>
            <Row gutter={[16, 16]}>
                <Col xs={24}>
                  <Form.Item
                    name="companyName"
                    label="Company Name"
                    rules={[{ required: true, message: 'Please enter company name' }]}
                  >
                    <Input placeholder="Enter company name" size="large" />
                </Form.Item>
              </Col>
                <Col xs={24}>
                  <Form.Item
                    name="companyCode"
                    label="Company ID"
                    validateFirst
                    validateDebounce={500}
                    extra={
                      canEditCompanyCode
                        ? 'Everyone in your company signs in with this ID. Changing it means all users must use the new ID.'
                        : 'Everyone in your company signs in with this ID. Only owners and admins can change it.'
                    }
                    rules={[
                      { required: true, message: 'Please enter a Company ID' },
                      {
                        pattern: /^[A-Za-z0-9][A-Za-z0-9-]{2,19}$/,
                        message: 'Use 3-20 characters: letters, numbers and dashes only',
                      },
                      {
                        // Redundancy check against other companies before saving.
                        validator: async (_rule, value) => {
                          const code = String(value || '').trim().toUpperCase();
                          if (!code) return;
                          if (code === String(companySettings.companyCode || '').toUpperCase()) return;
                          const res: any = await apiService.checkCompanyCode(code);
                          const data = res?.data || res;
                          if (data && data.available === false) {
                            throw new Error(data.reason || 'This Company ID is already taken');
                          }
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder="e.g., ACME"
                      size="large"
                      disabled={!canEditCompanyCode}
                      style={{ textTransform: 'uppercase' }}
                    />
                </Form.Item>
              </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="companyContactNumber"
                    label="Company Contact Number"
                    rules={[{ required: true, message: 'Please enter company contact number' }]}
                  >
                    <Input 
                      placeholder="Enter company contact number" 
                      prefix={<PhoneOutlined />}
                      size="large"
                    />
                </Form.Item>
              </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="companyEmail"
                    label="Company Email"
                    rules={[
                      { required: true, message: 'Please enter company email' },
                      { type: 'email', message: 'Please enter a valid email address' }
                    ]}
                  >
                    <Input 
                      placeholder="Enter company email" 
                      prefix={<MailOutlined />}
                      size="large"
                    />
                </Form.Item>
              </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="industry"
                    label="Industry"
                  >
                    <Select
                      placeholder="Select your industry"
                      size="large"
                      allowClear
                      showSearch
                    >
                      <Option value="Technology">Technology</Option>
                      <Option value="Healthcare">Healthcare</Option>
                      <Option value="Finance">Finance & Banking</Option>
                      <Option value="Education">Education</Option>
                      <Option value="Manufacturing">Manufacturing</Option>
                      <Option value="Retail">Retail & E-commerce</Option>
                      <Option value="Real Estate">Real Estate</Option>
                      <Option value="Marketing">Marketing & Advertising</Option>
                      <Option value="Consulting">Consulting</Option>
                      <Option value="Logistics">Logistics & Supply Chain</Option>
                      <Option value="Hospitality">Hospitality & Tourism</Option>
                      <Option value="Legal">Legal Services</Option>
                      <Option value="Other">Other</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    name="description"
                    label="Company Description"
                  >
                    <Input.TextArea
                      placeholder="Brief description of your company — this is used by AI to generate personalized emails"
                      rows={3}
                      showCount
                      maxLength={500}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    name="services"
                    label="Products & Services"
                  >
                    <Input.TextArea
                      placeholder="Describe your key products or services — AI will reference these in outreach emails"
                      rows={3}
                      showCount
                      maxLength={500}
                    />
                  </Form.Item>
                </Col>
            </Row>
      </Col>

            {/* Right side - Company Logo */}
            <Col xs={24} lg={8}>
              <Form.Item name="companyLogo" label="Company Logo">
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Upload
                    name="logo"
                    listType="picture-card"
                    showUploadList={false}
                    beforeUpload={handleLogoUpload}
                    accept="image/*"
                    style={{ width: '200px', height: '200px' }}
                  >
                    {logoPreview || companySettings.companyLogo ? (
                      <div style={{ 
                        width: '200px', 
                        height: '200px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        border: '2px dashed #d9d9d9',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <Image
                          src={logoPreview || companySettings.companyLogo}
                          alt="Company Logo"
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '100%', 
                            objectFit: 'contain' 
                          }}
                          preview={{
                            mask: <EyeOutlined />
                          }}
                        />
                      </div>
                    ) : (
                      <div style={{ 
                        width: '200px', 
                        height: '200px', 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'center',
                        border: '2px dashed #d9d9d9',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'border-color 0.3s'
                      }}>
                        <UploadOutlined style={{ fontSize: '32px', color: '#999', marginBottom: '8px' }} />
                        <div style={{ color: '#999', textAlign: 'center' }}>
                          Click to Upload Logo
                        </div>
                      </div>
                    )}
                  </Upload>
                </div>
                <div style={{ textAlign: 'center', marginTop: '8px', color: '#666', fontSize: '12px' }}>
                  Recommended size: 200x200px, supports JPG, PNG formats
                </div>
                </Form.Item>
              </Col>
            </Row>
          
          <Divider />
          
          <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />} size="large">
            Save Company Information
          </Button>
          </Form>
        </Card>

            {/* Company Addresses Card */}
            <Card 
        title="Company Addresses" 
        extra={
                  <Button 
                    type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddAddress}
                  >
            Add Address
                  </Button>
        }
      >
            <Table
          dataSource={companySettings.companyAddresses}
              columns={[
            {
              title: 'Type',
              dataIndex: 'type',
              key: 'type',
              render: (type: string) => {
                const typeMap = {
                  delivery: { label: 'Delivery', color: 'blue' },
                  correspondence: { label: 'Correspondence', color: 'green' },
                  billing: { label: 'Billing', color: 'orange' },
                  other: { label: 'Other', color: 'default' }
                };
                const typeInfo = typeMap[type as keyof typeof typeMap] || { label: type, color: 'default' };
                return (
                  <Tag color={typeInfo.color}>
                    {typeInfo.label}
                  </Tag>
                );
              }
            },
            {
              title: 'Contact Name',
              dataIndex: 'contactName',
              key: 'contactName'
            },
            {
              title: 'Address',
              key: 'address',
              render: (record: CompanyAddress) => (
                <div>
                  <div style={{ fontWeight: 500, marginBottom: '4px' }}>{record.detailedAddress}</div>
                  <div style={{ color: '#666', fontSize: '12px' }}>{record.country}</div>
                </div>
              )
            },
            {
              title: 'Phone',
              dataIndex: 'phone',
              key: 'phone'
            },
            {
              title: 'Default',
              key: 'default',
              render: (record: CompanyAddress) => (
                record.isDefault ? (
                  <Tag color="success">Default</Tag>
                ) : (
                  <Button 
                    size="small" 
                    onClick={() => handleSetDefaultAddress(record.id)}
                  >
                    Set Default
                  </Button>
                )
              )
            },
                {
                  title: 'Actions',
                  key: 'actions',
              render: (record: CompanyAddress) => (
                <Space>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => handleEditAddress(record)}
                  />
                  <Popconfirm
                    title={record.isDefault && companySettings.companyAddresses.length <= 1
                      ? 'Delete the only/default address? This cannot be undone.'
                      : 'Are you sure you want to delete this address?'}
                    onConfirm={() => handleDeleteAddress(record.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={loading}
                      loading={loading}
                    />
                  </Popconfirm>
                </Space>
              )
                }
              ]}
          rowKey="id"
              pagination={false}
          locale={{ emptyText: 'No addresses configured. Click "Add Address" to get started.' }}
            />
        </Card>
    </div>
  );

    const renderAddressModal = () => (
    <Modal
      title={editingAddress ? 'Edit Address' : 'Add New Address'}
      open={isAddressModalVisible}
      onCancel={() => setIsAddressModalVisible(false)}
      footer={null}
      width={600}
    >
      <Form
        form={addressForm}
        layout="vertical"
        onFinish={handleSaveAddress}
      >
            <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
                        <Form.Item
              name="type"
              label="Address Type"
              rules={[{ required: true, message: 'Please select address type' }]}
            >
              <Select placeholder="Select address type">
                <Option value="delivery">Delivery</Option>
                <Option value="correspondence">Correspondence</Option>
                <Option value="billing">Billing</Option>
                <Option value="other">Other</Option>
              </Select>
                </Form.Item>
              </Col>
          <Col xs={24} sm={12}>
                        <Form.Item
              name="contactName"
              label="Contact Name"
              rules={[{ required: true, message: 'Please enter contact name' }]}
            >
              <Input placeholder="Enter contact name" />
                </Form.Item>
              </Col>
          <Col xs={24} sm={12}>
                        <Form.Item
              name="country"
              label="Country/Region"
              rules={[{ required: true, message: 'Please select country/region' }]}
            >
              <Select placeholder="Select country/region" showSearch>
                <Option value="Hong Kong">Hong Kong</Option>
                <Option value="China">China</Option>
                <Option value="Taiwan">Taiwan</Option>
                <Option value="Macau">Macau</Option>
                <Option value="Singapore">Singapore</Option>
                <Option value="Malaysia">Malaysia</Option>
                <Option value="USA">USA</Option>
                <Option value="Canada">Canada</Option>
                <Option value="UK">UK</Option>
                <Option value="Australia">Australia</Option>
              </Select>
                </Form.Item>
              </Col>
          <Col xs={24} sm={12}>
                        <Form.Item
              name="phone"
              label="Phone Number"
            >
              <Input placeholder="Enter phone number (optional)" prefix={<PhoneOutlined />} />
                </Form.Item>
              </Col>
          <Col xs={24}>
                        <Form.Item
              name="detailedAddress"
              label="Detailed Address"
              rules={[{ required: true, message: 'Please enter detailed address' }]}
            >
              <Input.TextArea 
                rows={3} 
                placeholder="Enter detailed address including street, building name, floor and unit number"
                showCount
                maxLength={200}
              />
            </Form.Item>
              </Col>
            </Row>
        
        <Divider />
        
          <Space>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
            {editingAddress ? 'Update Address' : 'Add Address'}
          </Button>
          <Button onClick={() => setIsAddressModalVisible(false)}>
            Cancel
          </Button>
          </Space>
      </Form>
    </Modal>
  );

  const tabItems = [
    {
      key: 'profile',
      label: (
        <span>
          <UserOutlined />
          Profile
        </span>
      ),
      children: renderProfileSettings()
    },
    {
      key: 'company',
      label: (
        <span>
          <DatabaseOutlined />
          Company
        </span>
      ),
      children: renderCompanySettings()
    },
    {
      key: 'attributes',
      label: (
        <span>
          <TagsOutlined />
          Attributes
        </span>
      ),
      children: <CustomAttributesSettings />
    },
    {
      key: 'client-tiers',
      label: (
        <span>
          <StarOutlined />
          Client Tiers
        </span>
      ),
      children: <ClientTiersSettings />
    },
    {
      key: 'lead-credits',
      label: (
        <span>
          <StarOutlined />
          Lead Credits
        </span>
      ),
      children: <CreditAllocationPanel />
    },
    ...(canViewTeam
      ? [
          {
            key: 'team',
            label: (
              <span>
                <TeamOutlined />
                Team
              </span>
            ),
            children: <TeamManagement />,
          },
        ]
      : []),
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>System Settings</Title>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        tabBarExtraContent={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadCompany} disabled={loading}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={loading}
              onClick={async () => {
                try {
                  const values = await companyForm.validateFields();
                  await handleSaveCompanySettings(values);
                } catch {
                  // validate failed -> no-op
                }
              }}
            >
              Save All
            </Button>
          </Space>
        }
      />
      
      {/* Address Modal */}
      {renderAddressModal()}
    </div>
  );
};

export default SettingsOverview; 